import React, { useState } from 'react'
import { FormGroup, Popover, OverlayTrigger, FormControl } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { APPROVAL_ACTION_TYPE } from '~/types/workflow'
import { Select } from '~/components/Select/Select'
import ErrorMessage from '~/components/shared/ErrorMessage'
import SelectUserGroup from '~/components/SelectUserGroup'
import { FieldArray, useFormikContext, type FieldArrayRenderProps } from 'formik'
import SelectDestFolderField from './SelectDestFolderField'
import HtmlCell from '~/components/HtmlCell.tsx'
import EventTypeDescriptionPopover from '~/components/EventTypeDecriptionPopover.tsx'
import SetPropertyActions from './SetPropertyActions'

interface UserGroup {
  value: string | number
  label: string
}

interface ActionData {
  id?: number
  name?: string
}

export interface ApprovalAction {
  actionType: string
  actionData: string | ActionData | Array<{ metadataTemplateId: number | null; val: string }>
  usersGroups: UserGroup[]
}

interface ApprovalEventActionsProps {
  arrayHelpers: FieldArrayRenderProps
  values: {
    approveActions: ApprovalAction[]
  }
  isFromManageWFModals?: boolean
  isDisabled?: boolean
  disabled?: boolean
}

function ApprovalEventActions(props: ApprovalEventActionsProps) {
  // todo, check for arrayHelpers and values in props!
  const { arrayHelpers, values, isFromManageWFModals, isDisabled } = props
  const formikContext = useFormikContext()
  const { t: translate } = useTranslation()
  const [showPopover, setShowPopover] = useState(false)

  const actionTypes = [
    ...(isFromManageWFModals
      ? [{ value: 'APPROVEACTION_SELECT_EVENT', label: translate('APPROVEACTION_SELECT_EVENT') }]
      : []),
    { value: APPROVAL_ACTION_TYPE.COPY, label: translate('APPROVEACTION_COPY_DOC') },
    { value: APPROVAL_ACTION_TYPE.MOVE, label: translate('APPROVEACTION_MOVE_DOC') },
    { value: APPROVAL_ACTION_TYPE.SET_PROPERTY, label: translate('APPROVEACTION_SET_PROPERTY') },
    { value: APPROVAL_ACTION_TYPE.NOTIFY, label: translate('APPROVEACTION_NOTIFY_USERS_GROUPS') },
    { value: APPROVAL_ACTION_TYPE.NOTIFY_EXTERNAL_SYSTEM, label: translate('APPROVEACTION_NOTIFY_EXTERNAL_SYSTEM') }
  ]

  const addStep = React.useCallback(() => {
    const newStep: ApprovalAction = {
      actionType: '',
      actionData: '',
      usersGroups: []
    }
    arrayHelpers.push(newStep)
  }, [arrayHelpers])

  const removeStep = React.useCallback(
    (index: number) => {
      arrayHelpers.remove(index)
    },
    [arrayHelpers]
  )

  const updateStep = React.useCallback(
    (newData: ApprovalAction, index: number) => {
      arrayHelpers.replace(index, newData)
      isFromManageWFModals ? formikContext.validateForm() : null
    },
    [arrayHelpers, isFromManageWFModals, formikContext]
  )

  // Memoized onChange handler for SelectUserGroup
  const handleUserGroupChange = React.useCallback(
    (option: any, item: any, index: number) => {
      const newValue = option ? option : []
      if (
        !window._ || !window._.isEqual
          ? JSON.stringify(item.usersGroups) !== JSON.stringify(newValue)
          : !window._.isEqual(item.usersGroups, newValue)
      ) {
        item.usersGroups = newValue
        updateStep(item, index)
      }
    },
    [updateStep]
  )

  return (
    <FormGroup>
      <div className="custom-dynamic-actions-table">
        {values.approveActions.length ? (
          <div className="table-header">
            <span className="h event-type pl5">
              {translate('BROWSER_FILE_SETTINGS_APPROVERS_TH_EVENT_TYPE')}
              <OverlayTrigger
                trigger={['hover', 'focus']}
                placement="top"
                rootClose={true}
                overlay={
                  <Popover id="e7bb7699-9523-45-9b9e-1f0c8e45bcdc">
                    <EventTypeDescriptionPopover />
                  </Popover>
                }
              >
                <button
                  data-action={'show-info'}
                  className="info_hover btn-link ml5 mt2"
                  type="button"
                  onMouseEnter={(e) => setShowPopover(true)}
                  onMouseLeave={(e) => setShowPopover(false)}
                ></button>
              </OverlayTrigger>
            </span>
            {values.approveActions.length === 1 &&
            values.approveActions
              .map((item) => item.actionType)
              .includes(APPROVAL_ACTION_TYPE.NOTIFY_EXTERNAL_SYSTEM) ? (
              ''
            ) : (
              <span className="h event-type-val">
                {translate('BROWSER_FILE_SETTINGS_APPROVERS_TH_NOTIFICATION_RECIPIENTS')}
              </span>
            )}
          </div>
        ) : null}
        {values.approveActions.map((item: any, index) => (
          <div key={index} className="table-row pl5 pr15 py5">
            {!props.disabled && (
              <a
                className="close"
                onClick={(e) => {
                  e.preventDefault()
                  if (isDisabled) {
                    return false
                  } else {
                    removeStep(index)
                  }
                }}
              ></a>
            )}

            <div className="clearfix">
              <div className="h event-type pull-left pt0">
                <Select
                  options={actionTypes}
                  placeholder={translate('APPROVEACTION_SELECT_EVENT')}
                  showNewOptionAtTop={true}
                  isClearable={false}
                  searchable
                  id="f66bd4f2-8864-4287-9ad7-b51d1801320f"
                  name={`${arrayHelpers.name}.${index}.actionType`}
                  onChange={(option: any) => {
                    if (option.value === 'APPROVEACTION_SELECT_EVENT' && isFromManageWFModals) {
                      item.actionType = ''
                      item.actionData = ''
                      item.usersGroups = []
                      updateStep(item, index)
                      return
                    } else {
                      item.actionType = option ? option.value : ''
                      item.actionData =
                        item.actionType === APPROVAL_ACTION_TYPE.SET_PROPERTY
                          ? []
                          : item.actionType === APPROVAL_ACTION_TYPE.NOTIFY
                            ? ''
                            : item.actionType === APPROVAL_ACTION_TYPE.NOTIFY_EXTERNAL_SYSTEM
                              ? ''
                              : {}

                      if (isFromManageWFModals && item.actionType === APPROVAL_ACTION_TYPE.SET_PROPERTY) {
                        item.actionData = [{ metadataTemplateId: null, val: '' }]
                      }
                      updateStep(item, index)
                    }
                  }}
                  value={item.actionType}
                  isDisabled={isDisabled}
                />
                <ErrorMessage name={`${arrayHelpers.name}.${index}.actionType`} />
              </div>
              {item.actionType !== APPROVAL_ACTION_TYPE.NOTIFY_EXTERNAL_SYSTEM ? (
                <div className="h event-type-val pull-left">
                  <SelectUserGroup
                    isMulti={true}
                    value={item.usersGroups}
                    onChange={(option) => handleUserGroupChange(option, item, index)}
                    name={`${arrayHelpers.name}.${index}.usersGroups`}
                    isDisabled={isDisabled}
                  />
                  <ErrorMessage name={`${arrayHelpers.name}.${index}.usersGroups`} />
                </div>
              ) : (
                <div className="h event-type-val pull-left">
                  <div className="pl10 ml5">
                    <HtmlCell>{translate('EVENT_TYPE_NOTIFY_EXTERNAL_SYSTEM_DESCRIPTION_FOR_USERS')}</HtmlCell>
                  </div>
                </div>
              )}
            </div>
            <div className="py5">
              {item.actionType === APPROVAL_ACTION_TYPE.NOTIFY && (
                <FormControl
                  as="textarea"
                  maxLength={4094}
                  name={`${arrayHelpers.name}.${index}.actionData`}
                  onChange={(e) => {
                    item.actionData = e.target.value
                    updateStep(item, index)
                  }}
                  id="3950adae-0624-42d7-96fa-af2c6714fb26"
                  value={item.actionData as string}
                  rows={6}
                  disabled={isDisabled}
                />
              )}

              {[APPROVAL_ACTION_TYPE.MOVE, APPROVAL_ACTION_TYPE.COPY].indexOf(item.actionType) > -1 && (
                <>
                  <SelectDestFolderField
                    key={index}
                    index={index}
                    item={item as any}
                    updateStep={(updatedItem: any, idx: number) => {
                      updateStep(updatedItem as ApprovalAction, idx)
                    }}
                    isDisabled={isDisabled}
                  />
                  <ErrorMessage name={`${arrayHelpers.name}.${index}.actionData`} isErrMsgAObj={true} />
                </>
              )}

              {item.actionType === APPROVAL_ACTION_TYPE.SET_PROPERTY && (
                <>
                  <FieldArray name={`${arrayHelpers.name}.${index}.actionData`}>
                    {(arrayHelpers) => (
                      <SetPropertyActions
                        arrayHelpers={arrayHelpers}
                        values={item as any}
                        isFromManageWFModals={isFromManageWFModals}
                        isDisabled={isDisabled}
                      />
                    )}
                  </FieldArray>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {!props.disabled && (
        <button
          data-action={'add-event'}
          className="btn btn-chita btn-add-step btn-second at_btnAddStepApprover"
          type="button"
          onClick={addStep}
          disabled={isDisabled}
        >
          {translate('BROWSER_FILE_SETTINGS_APPROVERS_BTN_ADD_EVENT')}
        </button>
      )}
    </FormGroup>
  )
}

export default ApprovalEventActions
